import React, { useState, useEffect } from 'react';
import StudentLayout from '../../components/StudentLayout';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  PlayCircle as VideoIcon,
  Description as NotesIcon,
  Info as InfoIcon,
  Help as FAQIcon,
  Book as GlossaryIcon,
  MenuBook as GuideIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const StudentContent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contents, setContents] = useState([]);
  const [filteredContents, setFilteredContents] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  const contentTypes = [
    { value: 'all', label: 'All Content', icon: <InfoIcon /> },
    { value: 'video', label: 'Videos', icon: <VideoIcon /> },
    { value: 'notes', label: 'Notes', icon: <NotesIcon /> },
    { value: 'jewellery_costume_guide', label: 'Jewellery and Costume Guide', icon: <GuideIcon /> },
    { value: 'faq', label: 'FAQ', icon: <FAQIcon /> },
    { value: 'model_question_papers', label: 'Model Question Papers', icon: <GlossaryIcon /> },
    { value: 'newsletter', label: 'Newsletter', icon: <NotesIcon /> }
  ];

  // Map old content types to new tab values for backward compatibility
  const contentTypeMapping = {
    'costume_guide': 'jewellery_costume_guide',
    'jewellery_guide': 'jewellery_costume_guide',
    'glossary': 'model_question_papers'
  };

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    filterContent();
  }, [contents, activeTab]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/content', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContents(response.data.contents || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const filterContent = () => {
    if (activeTab === 'all') {
      setFilteredContents(contents);
    } else {
      setFilteredContents(contents.filter(c => {
        const mappedType = contentTypeMapping[c.content_type] || c.content_type;
        return mappedType === activeTab;
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getContentIcon = (type) => {
    const mappedType = contentTypeMapping[type] || type;
    const contentType = contentTypes.find(t => t.value === mappedType);
    return contentType ? contentType.icon : <InfoIcon />;
  };

  const getContentTypeLabel = (type) => {
    const mappedType = contentTypeMapping[type] || type;
    const contentType = contentTypes.find(t => t.value === mappedType);
    return contentType ? contentType.label : type;
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      public: 'success',
      student: 'info',
      batch_specific: 'warning'
    };
    return colors[level] || 'default';
  };

  const handleViewFile = async (contentId, fileName) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/content/download/${contentId}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create a blob URL and open in new tab for viewing only
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window/tab for viewing
      const viewWindow = window.open(url, '_blank');
      
      // Prevent download by disabling right-click in the new window
      if (viewWindow) {
        viewWindow.onload = () => {
          viewWindow.document.addEventListener('contextmenu', (e) => e.preventDefault());
        };
      }
      
      toast.success('Opening file for viewing');
    } catch (error) {
      toast.error('Failed to open file');
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Learning Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Access videos, notes, guides and learning materials
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filter Tabs */}
        <Card sx={{ 
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
          border: '1px solid',
          borderColor: 'rgba(212, 175, 55, 0.2)',
          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
          },
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ pl: 2 }}
          >
            {contentTypes.map(type => (
              <Tab
                key={type.value}
                label={type.label}
                value={type.value}
                icon={type.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Card>

        {/* Content Grid */}
        {filteredContents.length === 0 ? (
          <Card sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
            border: '1px solid',
            borderColor: 'rgba(212, 175, 55, 0.2)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
            },
          }}>
            <CardContent sx={{ pl: 3 }}>
              <Typography variant="body1" sx={{ color: '#8B7355' }} align="center" py={4}>
                No content available at this time. Check back later!
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredContents.map((content) => (
              <Grid item xs={12} sm={6} md={4} key={content.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                  border: '1px solid',
                  borderColor: 'rgba(212, 175, 55, 0.2)',
                  boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                    transform: 'translateY(-2px)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                  },
                }}>
                  <CardContent sx={{ flexGrow: 1, pl: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {getContentIcon(content.content_type)}
                      <Chip
                        label={getContentTypeLabel(content.content_type)}
                        size="small"
                        sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)' }}
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                      {content.title}
                    </Typography>
                    
                    {content.description && (
                      <Typography variant="body2" sx={{ color: '#8B7355' }} paragraph>
                        {content.description.length > 100
                          ? `${content.description.substring(0, 100)}...`
                          : content.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={content.access_level.replace('_', ' ')}
                        size="small"
                        color={getAccessLevelColor(content.access_level)}
                      />
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    {content.video_url ? (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<VideoIcon />}
                        href={content.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch Video
                      </Button>
                    ) : content.file_path ? (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewFile(content.id, content.file_path.split('/').pop())}
                      >
                        View
                      </Button>
                    ) : (
                      <Button variant="outlined" fullWidth disabled>
                        Coming Soon
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Statistics */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredContents.length} of {contents.length} learning materials
          </Typography>
        </Box>
      </Box>
    </StudentLayout>
  );
};

export default StudentContent;
